/*
 * Назначение: контекст текущего проекта.
 * Функционал: после авторизации загружает список проектов и выбирает
 * первый по умолчанию (или сохранённый в localStorage). При смене проекта
 * сбрасывает кэш TanStack Query и проставляет заголовок X-Project-Slug
 * для всех админ-вызовов.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { projectsApi } from "../../../lib/admin-api";
import { setAdminProjectSlug } from "../../../lib/admin-api";
import type { ProjectShort } from "../../../lib/types";
import { useAuth } from "../auth/auth-provider";

const STORAGE_KEY = "ai-seller-project-slug";

interface ProjectContextValue {
  projects: ProjectShort[];
  currentSlug: string | null;
  current: ProjectShort | null;
  setCurrent: (slug: string) => void;
  /** Перечитать список проектов (после переименования — обновить переключатель). */
  reloadProjects: () => void;
  loading: boolean;
  error: string | null;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const queryClient = useQueryClient();

  const [projects, setProjects] = useState<ProjectShort[]>([]);
  const [currentSlug, setCurrentSlug] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Подгружаем список проектов после логина
  useEffect(() => {
    if (status !== "authenticated") {
      setProjects([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    projectsApi
      .list()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        setError(null);
        const stored = localStorage.getItem(STORAGE_KEY);
        const found = list.find((p) => p.slug === stored);
        const next = found?.slug ?? list[0]?.slug ?? null;
        setCurrentSlug(next);
        setAdminProjectSlug(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.detail || "Не удалось загрузить проекты");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  // Любая смена slug — обновляем заголовок и localStorage, чистим кэш.
  // Кэш прежнего проекта удаляем, а не «инвалидируем»: invalidateQueries()
  // тут же перезапрашивал открытые запросы со старым ключом, но уже с новым
  // X-Project-Slug — в кэш прежнего проекта записывались данные нового, а
  // открытый диалог получал 404 («Не удалось загрузить»), и при возврате к
  // проекту список показывал чужие диалоги.
  const setCurrent = useCallback(
    (slug: string) => {
      const prev = currentSlug;
      if (prev === slug) return;
      void queryClient.cancelQueries();
      if (prev) {
        queryClient.removeQueries({ predicate: (q) => q.queryKey.includes(prev) });
      }
      setCurrentSlug(slug);
      localStorage.setItem(STORAGE_KEY, slug);
      setAdminProjectSlug(slug);
      // Запросы без проекта в ключе (["leads"], ["dashboard"] и т.п.) — перезапросить.
      queryClient.invalidateQueries({ predicate: (q) => !(prev && q.queryKey.includes(prev)) });
    },
    [queryClient, currentSlug],
  );

  // Тихое обновление списка без смены текущего проекта и без сброса кэша.
  const reloadProjects = useCallback(() => {
    projectsApi
      .list()
      .then((list) => setProjects(list))
      .catch(() => {
        /* не критично: список обновится при следующей загрузке страницы */
      });
  }, []);

  const current = useMemo(
    () => projects.find((p) => p.slug === currentSlug) ?? null,
    [projects, currentSlug],
  );

  const value = useMemo<ProjectContextValue>(
    () => ({ projects, currentSlug, current, setCurrent, reloadProjects, loading, error }),
    [projects, currentSlug, current, setCurrent, reloadProjects, loading, error],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error("useProject должен использоваться внутри ProjectProvider");
  }
  return ctx;
}
